import { useCallback, useEffect, useRef, useState } from "react";

import { isValidFormRedirectUrl } from "@smart/bridge-templates-basic";
import {
  aiFillSectionUri,
  defaultAIFillSettings,
  FormAIFillSettings,
  SubmissionStatus,
} from "@smart/bridge-types-basic";
import { designSystemTheme } from "@smart/itops-sb-design-system-dom";
import {
  delay,
  entriesOf,
  fromEntries,
  mapEntries,
  navigateFullPage,
} from "@smart/itops-utils-basic";

import { useDBResponses } from "./database-responses";
import { useFileUpload } from "./file-upload";
import { useLoadMatterFields } from "./load-matter-fields";
import { useVisibility } from "./visibility";
import { useSectionVisit } from "./visit";
import {
  AutoFillStatus,
  ConnectionItem,
  FieldItem,
  GroupItem,
  LoadResponses,
  ResponseSubmitter,
  SectionItem,
  SubmissionItem,
  SubmissionStatusItem,
} from "../types";

const SIGNATURE_ORDER_TOTAL_WAIT_MS = 70_000;

export type IntakeFormHookProps = {
  submission: SubmissionItem;
  sections: SectionItem[];
  groups: GroupItem[];
  fields: FieldItem[];
  connection?: ConnectionItem;
  disabled?: boolean;
  submitResponses: ResponseSubmitter;
  reloadForSigningUrl?: () => Promise<string | undefined>;
  loadResponses: LoadResponses;
  shouldLoadDBResponses?: boolean;
  postSubmissionStatus?: SubmissionStatusItem;
  previewLastSection?: [
    SectionItem | "summary" | undefined,
    React.Dispatch<React.SetStateAction<SectionItem | "summary" | undefined>>,
  ];
  isPreview?: boolean;
  aiFillSettings?: FormAIFillSettings;
  runAutoFilling?: (uploadedFileKeys: string[]) => Promise<void>;
  resetAutoFilling?: () => void;
  autoFillStatus?: AutoFillStatus;
  submitAutofillFeedback?: (feedback?: string) => Promise<void>;
  selectedLanguage?: string;
  redirectUrl?: string;
  formTranslations?: {
    language: string;
    title?: string;
    response?: string;
    aiFillSectionTitle?: string;
    aiFillFileUploadLabel?: string;
    aiFillFileUploadDescription?: string;
  }[];
};

export const useIntakeForm = ({
  submission,
  sections,
  fields,
  groups,
  connection,
  disabled,
  submitResponses,
  reloadForSigningUrl,
  loadResponses,
  shouldLoadDBResponses,
  postSubmissionStatus,
  previewLastSection,
  isPreview,
  aiFillSettings,
  runAutoFilling,
  resetAutoFilling,
  autoFillStatus,
  submitAutofillFeedback,
  selectedLanguage,
  redirectUrl,
  formTranslations,
}: IntakeFormHookProps) => {
  const [summary, setSummary] = useState(previewLastSection?.[0] === "summary");
  const [responses, setResponses] = useState(() =>
    submission.responses
      ? mapEntries<Record<string, { value: any }>, Record<string, any>>(
          submission.responses,
          ({ value }) => value,
        )
      : {},
  );
  const [status, setStatus] = useState<SubmissionStatus | undefined>(
    submission.status || "active",
  );
  const [redirected, setRedirected] = useState(false);
  const [signaturePreparingStatus, setSignaturePreparingStatus] = useState<
    "waiting" | "failed" | undefined
  >();
  const signatureWaitStartedAtRef = useRef<number | undefined>(undefined);

  const uploadFileProps = useFileUpload();
  const visibility = useVisibility({ sections, groups, fields, responses });
  const visibleSections = sections
    .filter((s) => !!visibility.visibleSections[s.uri])
    .map((section) => ({
      ...section,
      hasRequired: !!fields.find(
        (f) => f.sectionUri === section.uri && f.mandatory,
      ),
    }));

  const formTranslation =
    selectedLanguage && selectedLanguage !== "en"
      ? formTranslations?.find((t) => t.language === selectedLanguage)
      : undefined;

  const aiFillSectionTitle =
    formTranslation?.aiFillSectionTitle ||
    aiFillSettings?.sectionTitle ||
    defaultAIFillSettings.sectionTitle;

  const aiFillFileUploadDescription =
    formTranslation?.aiFillFileUploadDescription ||
    aiFillSettings?.fileUploadDescription ||
    defaultAIFillSettings.fileUploadDescription;

  const autoFillSection: SectionItem | undefined =
    aiFillSettings?.allowAiFill && visibleSections.length > 0
      ? {
          uri: aiFillSectionUri,
          title: aiFillSectionTitle,
        }
      : undefined;
  const [autofillUnavailable, setAutofillUnavailable] = useState(false);

  const [sidebar, setSidebar] = useState(
    isPreview || window.innerWidth < designSystemTheme.breakPoints.tablet
      ? false
      : visibleSections.length > 1 || !!autoFillSection,
  );

  const previewLastAsSection =
    previewLastSection?.[0] !== "summary" ? previewLastSection?.[0] : undefined;
  const previewLastStillVisible =
    previewLastAsSection &&
    (previewLastAsSection.uri === aiFillSectionUri && autoFillSection
      ? previewLastAsSection
      : visibleSections.find((s) => s.uri === previewLastAsSection.uri));

  const [selected, setSelected] = useState<SectionItem | undefined>(
    (submission.lastUpdatedSectionUri &&
      visibleSections.find(
        (s) => s.uri === submission.lastUpdatedSectionUri,
      )) ||
      previewLastStillVisible ||
      (!submission.autoFillStatus && autoFillSection) ||
      visibleSections[0],
  );

  // firm post-submit redirect, false when URL missing or invalid
  const redirectToFirmUrl = useCallback(() => {
    if (!redirectUrl) return false;
    if (!isValidFormRedirectUrl(redirectUrl)) {
      console.error("Invalid redirect URL: ", redirectUrl);
      return false;
    }
    setRedirected(true);
    navigateFullPage(redirectUrl);
    return true;
  }, [redirectUrl]);

  const completeFormOrRedirect = useCallback(
    (submissionStatus: SubmissionStatus | undefined, signingUrl?: string) => {
      if (
        submissionStatus === "completed" &&
        signingUrl &&
        !isPreview &&
        isValidFormRedirectUrl(signingUrl)
      ) {
        setRedirected(true);
        window.location.assign(signingUrl);
        return;
      }

      setSignaturePreparingStatus(undefined);

      if (submissionStatus === "completed" && redirectUrl && !isPreview) {
        if (!redirectToFirmUrl()) setStatus(submissionStatus);
      } else {
        setStatus(submissionStatus);
      }
    },
    [redirectUrl, isPreview, redirectToFirmUrl],
  );

  /**
   * The digital signature order may take more than 30 seconds to complete,
   * which will exceed the API Gateway's hard 30s integration timeout.
   * If that happens, we wait for up to 70 seconds before giving up and showing an error.
   */
  const beginDigitalSignatureWait = useCallback(() => {
    signatureWaitStartedAtRef.current = Date.now();
    setSignaturePreparingStatus("waiting");
  }, []);

  const attemptDigitalSignatureRecovery = useCallback(async () => {
    const startedAt = signatureWaitStartedAtRef.current;
    const elapsed = startedAt ? Date.now() - startedAt : 0;

    if (!reloadForSigningUrl) {
      setSignaturePreparingStatus("failed");
      return;
    }

    await delay(Math.max(SIGNATURE_ORDER_TOTAL_WAIT_MS - elapsed, 0));
    const signingUrl = await reloadForSigningUrl().catch(console.error);

    if (signingUrl && isValidFormRedirectUrl(signingUrl)) {
      completeFormOrRedirect("completed", signingUrl);
    } else {
      setSignaturePreparingStatus("failed");
    }
  }, [reloadForSigningUrl, completeFormOrRedirect]);

  const {
    saveTimestampAndSubmit,
    databaseResponses,
    isLoading: isLoadingDBResponses,
  } = useDBResponses({
    submitResponses,
    loadResponses,
    shouldLoad: !!shouldLoadDBResponses && summary,
  });

  const {
    isUpdating,
    isAllVisited,
    isSectionVisited,
    isSectionVisiting,
    setSectionVisited,
    setSectionVisiting,
    isNavigatable,
  } = useSectionVisit({
    selected,
    visibleSections,
    visibleFields: fields.filter((f) => !!visibility.visibleFields[f.uri]),
    lastUpdatedSectionUri: submission.lastUpdatedSectionUri || selected?.uri,
    responses,
  });

  const onSelect = (section: SectionItem) => {
    if (
      section.uri === aiFillSectionUri &&
      (!!autoFillStatus || !!submission.autoFillStatus)
    ) {
      setAutofillUnavailable(true);
      return;
    }

    setSectionVisiting(section);
    setSummary(false);
    setSelected(section);
    if (previewLastSection) previewLastSection[1](section);
  };

  const onSummary = () => {
    setSummary(true);
    if (previewLastSection) previewLastSection[1]("summary");
  };

  useEffect(() => {
    if (summary) return;
    if (!selected?.uri) return;
    if (selected.uri === aiFillSectionUri && autoFillSection) return;
    if (visibleSections.some((s) => s.uri === selected.uri)) return;
    const fallback = visibleSections[0];
    if (fallback) {
      if (
        fallback.uri === aiFillSectionUri &&
        (!!autoFillStatus || !!submission.autoFillStatus)
      ) {
        setAutofillUnavailable(true);
        return;
      }
      setSectionVisiting(fallback);
      setSummary(false);
      setSelected(fallback);
      if (previewLastSection) previewLastSection[1](fallback);
    } else {
      setSelected(undefined);
      previewLastSection?.[1](undefined);
    }
  }, [
    visibleSections,
    selected,
    summary,
    previewLastSection,
    autoFillStatus,
    submission.autoFillStatus,
    setSectionVisiting,
    autoFillSection,
  ]);

  const onRunAutoFilling = async (fileKeys: string[]) => {
    if (!runAutoFilling) return;

    try {
      await runAutoFilling(fileKeys);
    } catch (error) {
      console.error("Error running auto filling. ", error);
    }
  };

  let printUnavailable: string | undefined;
  if (!disabled && (status !== "completed" || !!submission.deleted)) {
    printUnavailable = "Saving a copy of the form is not available.";
  } else if (status === "completed" && !submission.updatedAt) {
    // The load lambda will remove the updatedAt field when the status is completed
    // so we know the user has closed the page
    printUnavailable = `Saving a copy of the form is not available after closing the page.`;
  }

  useLoadMatterFields(
    {
      submissionSyncStatus: submission.syncStatus,
      fn: () => {
        if (submission.responses) {
          setResponses((currentResponses) => {
            const loadedResponses = fromEntries(
              entriesOf(submission.responses!)
                .filter(
                  ([fieldUri, { syncStatus }]) =>
                    !currentResponses[fieldUri] && syncStatus === "loaded",
                )
                .map(([fieldUri, { value }]) => [fieldUri, value]),
            );

            return {
              ...currentResponses,
              ...loadedResponses,
            };
          });
        }
      },
    },
    [submission.responses],
  );

  useEffect(() => {
    if (autoFillStatus && selected?.uri === aiFillSectionUri) {
      onSelect(visibleSections[0]);
    }
  }, [autoFillStatus]);

  useEffect(() => {
    if (!aiFillSettings?.allowAiFill && selected?.uri === aiFillSectionUri) {
      onSelect(visibleSections[0]);
    }
  }, [aiFillSettings?.allowAiFill]);

  useEffect(() => {
    if (autoFillStatus === "filling" && submission.responses) {
      setResponses(
        mapEntries<Record<string, { value: any }>, Record<string, any>>(
          submission.responses,
          ({ value }) => value,
        ),
      );
    }
  }, [autoFillStatus, submission.responses]);

  return {
    status: postSubmissionStatus?.status || status,
    summary,
    statusProps: {
      status: postSubmissionStatus?.status || status,
      deleted:
        postSubmissionStatus?.deleted !== undefined
          ? postSubmissionStatus?.deleted
          : submission.deleted,
      autoFillStatus,
      redirected,
      onSubmitAutofillFeedback: submitAutofillFeedback,
    },
    signaturePreparingStatus,
    sidebarProps: {
      autoFillSection,
      visibleSections,
      selected,
      onSelect,
      summary,
      onSummary,
      sidebar,
      setSidebar,
      isUpdating,
      isAllVisited,
      isNavigatable,
      isSectionVisited,
      isSectionVisiting,
      isAutoFillSectionSelected: selected?.uri === aiFillSectionUri,
      autoFillStatus,
    },
    summaryProps: {
      connection,
      sections,
      groups,
      fields,
      onSelect,
      submitResponses: saveTimestampAndSubmit,
      isLoadingDBResponses,
      completeFormOrRedirect,
      beginDigitalSignatureWait,
      attemptDigitalSignatureRecovery,
      sidebar,
      responses: shouldLoadDBResponses ? databaseResponses : responses,
      visibility,
      disabled,
      autoFillStatus,
    },
    sectionProps: {
      key:
        autoFillStatus === "filling"
          ? JSON.stringify(responses)
          : selected?.uri,
      connection,
      autoFillSection,
      visibleSections,
      isUpdating,
      selected,
      groups,
      fields,
      onSelect,
      onSummary,
      submitResponses: saveTimestampAndSubmit,
      setResponses,
      completeFormOrRedirect,
      beginDigitalSignatureWait,
      attemptDigitalSignatureRecovery,
      setStatus,
      setVisited: setSectionVisited,
      sidebar,
      setSidebar,
      responses,
      visibility,
      disabled,
      autoFillStatus,
    },
    printProps: {
      content: {
        sections,
        groups,
        fields,
        visibility,
        responses,
      },
      disabled: disabled || autoFillStatus === "filling",
      unavailable: printUnavailable,
    },
    uploadFileProps,
    autoFillProps: {
      autoFillSection,
      sidebar,
      setSidebar,
      description: aiFillFileUploadDescription,
      isAutoFillSectionSelected: selected?.uri === aiFillSectionUri,
      autoFillStatus,
      disabled,
      onSkip: () => {
        if (sections.length) onSelect(sections[0]);
      },
      onSubmit: onRunAutoFilling,
      onReset: () => {
        if (resetAutoFilling) resetAutoFilling();
      },
    },
    autoFillUnavailableProps: {
      open: autofillUnavailable,
      onClose: () => setAutofillUnavailable(false),
    },
  };
};
